import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, Calendar, Clock, CheckCircle2, Award, ExternalLink, Info, MapPin } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { useLanguage } from "../context/LanguageContext";
import api from "../api/client";

function pickLocalized(profile, lang) {
  if (!profile?.i18n) return null;
  return profile.i18n[lang] || profile.i18n.fr || null;
}

export default function StaffDetail() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [centre, setCentre] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/staff/${slug}`)
      .then((r) => {
        setProfile(r.data);
        const cslug = r.data.centre_slug || "cinquantenaire";
        api.get(`/centres/${cslug}`).then((c) => setCentre(c.data)).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <Navigate to="/equipe" replace />;
  if (!profile) {
    return (
      <div className="min-h-screen bg-white font-jakarta">
        <Navbar />
        <div className="pt-32 max-w-3xl mx-auto px-6 py-12 space-y-4">
          <div className="h-10 bg-slate-100 rounded-full w-2/3 animate-pulse" />
          <div className="h-64 bg-slate-100 rounded-3xl animate-pulse" />
        </div>
      </div>
    );
  }

  const localized = pickLocalized(profile, language) || {};
  const title = localized.title || profile.specialty || profile.name;
  const isExternal = profile.appointmentUrl?.startsWith("http");
  const gradient = profile.gradient || "from-blue-500 to-cyan-500";
  const accent = profile.accent || profile.color || "bg-blue-50 text-blue-700";

  return (
    <div className="min-h-screen bg-white font-jakarta" data-testid="staff-detail">
      <Navbar />
      <Seo title={`${profile.name} – ${title}`} description={(localized.paragraphs?.[0] || `${profile.name}, ${title}${centre ? ` – ${centre.name}, ${centre.city}` : ""}`).slice(0, 160)} image={profile.photo} />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-blue-50/40">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <Link
            to="/equipe"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#2E4DB5] transition-colors mb-8"
            data-testid="staff-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.staff.back_to_team}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 items-start">
            <div className="relative">
              <div className={`absolute -inset-3 bg-gradient-to-br ${gradient} opacity-20 blur-2xl rounded-full`}></div>
              <div className="relative w-full aspect-square overflow-hidden rounded-3xl shadow-xl bg-slate-100">
                <img src={profile.photo} alt={profile.name} loading="eager" className="w-full h-full object-cover object-top" />
              </div>
            </div>

            <div>
              <span className={`inline-block text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4 ${accent}`}>
                {title}
              </span>
              <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-slate-900 leading-tight" data-testid="staff-detail-name">
                {profile.name}
              </h1>

              {centre && (
                <Link
                  to={`/centres/${centre.slug}`}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-600 hover:border-[#3B9DA5] hover:text-[#2E4DB5] transition-colors group"
                  data-testid="staff-detail-centre"
                >
                  <MapPin className="w-4 h-4 text-[#3B9DA5]" />
                  <span>{t.staff.practice_at}</span>
                  <span className="font-semibold text-slate-800 group-hover:text-[#2E4DB5]">{centre.name}</span>
                  {centre.address_line && <span className="text-slate-400 hidden sm:inline">— {centre.address_line}</span>}
                </Link>
              )}

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                <a href={`tel:${profile.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-100 rounded-2xl hover:border-[#3B9DA5] transition-colors group" data-testid="staff-detail-phone">
                  <span className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50">
                    <Phone className="w-4 h-4 text-[#3B9DA5]" />
                  </span>
                  <span className="text-sm text-slate-700 font-medium">{profile.phone}</span>
                </a>
                <a href={`mailto:${profile.email}`} className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-100 rounded-2xl hover:border-[#3B9DA5] transition-colors group" data-testid="staff-detail-email">
                  <span className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center group-hover:bg-blue-50">
                    <Mail className="w-4 h-4 text-[#3B9DA5]" />
                  </span>
                  <span className="text-sm text-slate-700 font-medium break-all">{profile.email}</span>
                </a>
              </div>

              {profile.schedule && (
                <div className="mt-4 inline-flex items-start gap-3 bg-white border border-slate-100 rounded-2xl px-4 py-3">
                  <span className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center mt-0.5">
                    <Clock className="w-4 h-4 text-[#3B9DA5]" />
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{t.staff.schedule}</p>
                    <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{profile.schedule}</p>
                  </div>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                {profile.appointmentUrl && (
                  <a
                    href={profile.appointmentUrl}
                    target={isExternal ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 gradient-bg text-white text-sm font-semibold px-6 py-3 rounded-full hover:opacity-90 transition-opacity"
                    data-testid="staff-detail-book"
                  >
                    <Calendar className="w-4 h-4" />
                    {t.staff.appointment}
                    {isExternal && <ExternalLink className="w-3.5 h-3.5" />}
                  </a>
                )}
                {profile.phoneAlt && (
                  <a href={`tel:${profile.phoneAlt.replace(/\s/g, "")}`} className="inline-flex items-center gap-2 border border-slate-200 text-slate-700 text-sm font-semibold px-6 py-3 rounded-full hover:bg-slate-50 transition-colors">
                    <Phone className="w-4 h-4" />
                    {profile.phoneAlt}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {localized.paragraphs && localized.paragraphs.length > 0 && (
        <section className="py-14">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6">{t.staff.about}</h2>
            <div className="space-y-5 text-slate-700 leading-relaxed text-[15px]">
              {localized.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </section>
      )}

      {localized.qualifications && localized.qualifications.length > 0 && (
        <section className="py-14 bg-slate-50/60">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-5 h-5 text-[#3B9DA5]" />
              <h2 className="font-outfit text-2xl font-bold text-slate-900">{t.staff.qualifications}</h2>
            </div>
            <ul className="space-y-3">
              {localized.qualifications.map((q, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 text-[15px]">
                  <CheckCircle2 className="w-5 h-5 text-[#3B9DA5] flex-shrink-0 mt-0.5" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {localized.domains && localized.domains.length > 0 && (
        <section className="py-14">
          <div className="max-w-3xl mx-auto px-6 sm:px-8">
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6">{t.staff.domains}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {localized.domains.map((d, i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-3 bg-white border border-slate-100 rounded-2xl">
                  <span className={`w-2 h-2 rounded-full mt-2 bg-gradient-to-br ${gradient}`}></span>
                  <span className="text-sm text-slate-700">{d}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {(localized.memberships?.length > 0 || profile.registrations?.length > 0) && (
        <section className="py-14 bg-slate-50/60">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 grid grid-cols-1 sm:grid-cols-2 gap-10">
            {localized.memberships?.length > 0 && (
              <div>
                <h3 className="font-outfit text-sm font-semibold text-slate-500 uppercase tracking-widest mb-4">{t.staff.memberships}</h3>
                <ul className="space-y-2 text-slate-700 text-sm">
                  {localized.memberships.map((m, i) => <li key={i}>{m}</li>)}
                </ul>
              </div>
            )}
            {profile.registrations?.length > 0 && (
              <div>
                <h3 className="font-outfit text-sm font-semibold text-slate-500 uppercase tracking-widest mb-4">{t.staff.registrations}</h3>
                <ul className="space-y-2 text-slate-700 text-sm font-mono">
                  {profile.registrations.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {localized.notes && localized.notes.length > 0 && (
        <section className="py-14">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 bg-blue-50/60 rounded-3xl p-6 sm:p-8 border border-blue-100">
            <div className="flex items-center gap-3 mb-4">
              <Info className="w-5 h-5 text-[#2E4DB5]" />
              <h3 className="font-outfit text-lg font-semibold text-slate-900">{t.staff.information}</h3>
            </div>
            <ul className="space-y-2 text-slate-700 text-sm leading-relaxed">
              {localized.notes.map((n, i) => <li key={i}>— {n}</li>)}
            </ul>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
